// ./views/Advertising/Advertising.js
import React, { useMemo, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Grid,
    Card,
    CardActionArea,
    CardContent,
    CircularProgress,
    Typography,
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import GrassIcon from '@mui/icons-material/Grass';
import LayersIcon from '@mui/icons-material/Layers';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';

import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { PermissionContext } from '../../context/PermissionContext';
import { PERMISSION_MAP } from '../../context/Mappings/permissionMap';

const Advertising = () => {
    const navigate = useNavigate();
    const [clickedCard, setClickedCard] = useState(null);
    const { logout } = useAuth();

    const { allow, loading } = useContext(PermissionContext);

    const menuOptions = useMemo(
        () => [
            { label: 'Dashboard', icon: <BarChartIcon fontSize="large" />, route: '/dashboard' },
            {
                label: 'Mapeo Maquinaria',
                icon: <AgricultureIcon fontSize="large" />,
                route: '/mapeo?tipo=mapeo_maquinaria',
            },
            {
                label: 'Procesamiento de fotos',
                icon: <GrassIcon fontSize="large" />,
                route: '/mapeo?tipo=procesamiento_de_fotos',
            },
            {
                label: 'SIG Agrícola',
                icon: <LayersIcon fontSize="large" />,
                route: '/mapeo?open=SIGAgricola',
            },
            {
                label: 'Historial',
                icon: <HistoryIcon fontSize="large" />,
                route: '/mapeo?open=historial',
            },
            { label: 'Configuración', icon: <SettingsIcon fontSize="large" />, route: '/configuracion' },
            { label: 'Cerrar Sesión', icon: <ExitToAppIcon fontSize="large" />, route: '/logout' },
        ],
        []
    );

    const sigSubs = useMemo(() => {
        const m = PERMISSION_MAP.find((mm) => mm.moduleKey === 'sig_agricola');
        return (m?.submodules || []).map((s) => s.submoduleKey);
    }, []);

    const visibleOptions = useMemo(() => {
        if (loading) return [];
        return menuOptions.filter((opt) => {
            switch (opt.label) {
                case 'Dashboard':
                    return allow('dashboard', null);
                case 'Mapeo Maquinaria':
                    return allow('mapeo', 'mapeo_maquinaria');

                // ✅ igual que el sidebar: siempre visible, sin depender del servicio de permisos
                case 'Procesamiento de fotos':
                    return true;

                case 'SIG Agrícola': {
                    if (!allow('sig_agricola', null)) return false;
                    return sigSubs.some((sk) => allow('sig_agricola', sk));
                }
                case 'Historial':
                    return allow('historial', null);
                case 'Configuración':
                    return allow('configuracion', null);
                case 'Cerrar Sesión':
                    return true;
                default:
                    return true;
            }
        });
    }, [menuOptions, allow, loading, sigSubs]);

    const handleOptionClick = (option) => {
        setClickedCard(option.label);
        setTimeout(() => {
            if (option.label === 'Cerrar Sesión') {
                logout();
                navigate('/login');
                return;
            }
            navigate(option.route);
        }, 600);
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '100vh',
                margin: 0,
                padding: 0,
                position: 'relative',
                overflow: 'hidden',
                backgroundImage: 'url("/background-menu.jpg")',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    zIndex: 1,
                    pointerEvents: 'none',
                }}
            />

            <Box
                sx={{
                    position: 'fixed',
                    top: { xs: '5vh', md: '8vh' },
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 3,
                    textAlign: 'center',
                }}
            >
                <img
                    src="/logo_oddis.png"
                    alt="Oddis Maps"
                    style={{
                        maxWidth: '190px',
                        maxHeight: '230px',
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                    }}
                />
            </Box>

            {loading && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 2,
                        backdropFilter: 'blur(1px)',
                    }}
                >
                    <CircularProgress />
                    <Typography variant="body2" sx={{ color: '#fff' }}>
                        Cargando permisos...
                    </Typography>
                </Box>
            )}

            <Box
                sx={{
                    position: 'absolute',
                    top: { xs: 'calc(18vh + 150px)', md: 'calc(22vh + 150px)' },
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    zIndex: 2,
                }}
            >
                <Grid
                    container
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ maxWidth: 900, px: 2 }}
                >
                    {!loading && visibleOptions.length === 0 && (
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    backgroundColor: 'rgba(255,255,255,0.6)',
                                    p: 2,
                                    borderRadius: 2,
                                    textAlign: 'center',
                                }}
                            >
                                <Typography variant="body1">
                                    No tienes opciones visibles por ahora. Verifica tus permisos.
                                </Typography>
                            </Box>
                        </Grid>
                    )}

                    {visibleOptions.map((option, idx) => (
                        <Grid item xs={6} sm={4} md={3} key={option.label}>
                            <motion.div
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: idx * 0.1 }}
                            >
                                <Card
                                    sx={{
                                        borderRadius: 4,
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        backdropFilter: 'blur(4px)',
                                        cursor: 'pointer',
                                        boxShadow:
                                            clickedCard === option.label
                                                ? '0 0 20px rgba(0,0,0,0.3)'
                                                : '0 2px 10px rgba(0,0,0,0.2)',
                                        transition:
                                            'background-color 0.3s ease, box-shadow 0.3s ease, transform 0.2s',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 1)',
                                            transform: 'translateY(-4px)',
                                        },
                                        height: 140,
                                        display: 'flex',
                                        alignItems: 'stretch',
                                    }}
                                >
                                    <CardActionArea
                                        onClick={() => handleOptionClick(option)}
                                        sx={{ height: '100%' }}
                                    >
                                        <CardContent
                                            sx={{
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                p: 2,
                                                textAlign: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            {option.icon}
                                            <Typography
                                                component="span"
                                                sx={{
                                                    fontWeight: 500,
                                                    lineHeight: 1.2,
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {option.label}
                                            </Typography>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Box>
    );
};

export default Advertising;
